import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Mail, Phone, MapPin, Clock, Send, Loader2, 
  CheckCircle, MessageSquare, HelpCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const contactReasons = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'booking', label: 'Booking Help' },
  { value: 'host', label: 'Becoming a Host' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'dispute', label: 'Report a Problem' },
  { value: 'partnership', label: 'Partnership Inquiry' },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    reason: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.reason || !form.message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
    setSubmitted(true);
    toast.success('Message sent! We\'ll get back to you soon.');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-farmhouse-moss/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="font-serif text-3xl font-semibold text-farmhouse-cream mb-4">
            Message Sent!
          </h1>
          <p className="text-farmhouse-muted mb-6">
            Thank you for reaching out. Our team will review your message and get back to you within 24-48 hours.
          </p>
          <Button onClick={() => setSubmitted(false)} className="btn-secondary">
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal" data-testid="contact-page">
      {/* Hero */}
      <section className="pt-16 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-4">
            Get in Touch
          </p>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-farmhouse-cream mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-farmhouse-muted max-w-2xl mx-auto">
            Have questions or feedback? We'd love to hear from you. Our team is here to help.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="md:col-span-1 space-y-6">
              <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
                <h3 className="font-serif text-lg font-semibold text-farmhouse-cream mb-4">
                  Contact Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-farmhouse-saddle/10">
                      <Mail className="w-5 h-5 text-farmhouse-saddle" />
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Email</p>
                      <a href="mailto:support@barnsandpastures.com" className="text-farmhouse-cream hover:text-farmhouse-saddle transition-colors">
                        support@barnsandpastures.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-farmhouse-saddle/10">
                      <Phone className="w-5 h-5 text-farmhouse-saddle" />
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Phone</p>
                      <a href="tel:+18005551234" className="text-farmhouse-cream hover:text-farmhouse-saddle transition-colors">
                        1-800-555-1234
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-farmhouse-saddle/10">
                      <Clock className="w-5 h-5 text-farmhouse-saddle" />
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Support Hours</p>
                      <p className="text-farmhouse-cream">Mon-Fri: 8am-8pm EST</p>
                      <p className="text-farmhouse-cream">Sat-Sun: 9am-5pm EST</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-farmhouse-saddle/10">
                      <MapPin className="w-5 h-5 text-farmhouse-saddle" />
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Headquarters</p>
                      <p className="text-farmhouse-cream">
                        123 Ranch Road<br />
                        Lexington, KY 40502
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
                <h3 className="font-serif text-lg font-semibold text-farmhouse-cream mb-4">
                  Quick Links
                </h3>
                <div className="space-y-3">
                  <a href="/help" className="flex items-center gap-2 text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    <HelpCircle className="w-4 h-4" />
                    Help Center & FAQs
                  </a>
                  <a href="/how-it-works" className="flex items-center gap-2 text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    <MessageSquare className="w-4 h-4" />
                    How It Works
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-2">
              <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-8">
                <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream mb-6">
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-farmhouse-cream">Your Name *</Label>
                      <Input
                        type="text"
                        placeholder="John Doe"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="form-input mt-1"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label className="text-farmhouse-cream">Email Address *</Label>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="form-input mt-1"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-farmhouse-cream">What can we help you with? *</Label>
                    <Select
                      value={form.reason}
                      onValueChange={(value) => setForm({ ...form, reason: value })}
                    >
                      <SelectTrigger className="form-input mt-1" data-testid="contact-reason">
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent className="bg-farmhouse-paper border-farmhouse-subtle">
                        {contactReasons.map((reason) => (
                          <SelectItem 
                            key={reason.value} 
                            value={reason.value}
                            className="text-farmhouse-cream hover:bg-farmhouse-subtle"
                          >
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-farmhouse-cream">Your Message *</Label>
                    <Textarea
                      placeholder="Tell us how we can help..."
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="form-input mt-1 min-h-[150px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="btn-primary w-full md:w-auto"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
